import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

function statusBadge(s) {
  const map = { Paused: 'badge-amber', Stopped: 'badge-red', Closed: 'badge-red' }
  return 'badge ' + (map[s] || 'badge-gray')
}

function typeBadge(t) {
  const map = { Marketing: 'badge-blue', Development: 'badge-purple', Hosting: 'badge-green' }
  return 'badge ' + (map[t] || 'badge-gray')
}

const FILTERS = [
  { key: 'search', label: 'Search', type: 'text', fields: ['client_name', 'brand_name', 'service_type'] },
  { key: 'status', label: 'Status', type: 'select', options: ['Paused', 'Stopped', 'Closed'] },
  { key: 'service_type', label: 'Type', type: 'select', options: ['Marketing', 'Development', 'Hosting'] },
]

export default function PausedClosed() {
  // services-based records (new model)
  const [services, setServices] = useState([])
  // legacy paused_closed records (old model — shown for backward compat)
  const [legacy, setLegacy] = useState([])
  const [clientsMap, setClientsMap] = useState({})
  const [brandsMap, setBrandsMap] = useState({})

  const [filtered, setFiltered] = useState([])
  const [reactivating, setReactivating] = useState(null)

  const handleFiltered = useCallback(d => setFiltered(d), [])

  useEffect(() => {
    async function load() {
      const [sRes, cRes, bRes, legacyRes] = await Promise.all([
        supabase.from('services')
          .select('*')
          .in('status', ['Paused', 'Stopped'])
          .order('created_at', { ascending: false }),
        supabase.from('clients').select('id, name'),
        supabase.from('brands').select('id, name'),
        supabase.from('paused_closed').select('*').order('date_paused', { ascending: false }),
      ])
      const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
      const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })
      setClientsMap(cm)
      setBrandsMap(bm)
      setServices((sRes.data || []).map(s => ({
        ...s,
        client_name: cm[s.client_id] || '—',
        brand_name: bm[s.brand_id] || '—',
        _source: 'services',
      })))
      setLegacy((legacyRes.data || []).map(p => ({
        ...p,
        service_type: p.category || 'Marketing',
        brand_name: p.project_name,
        _source: 'legacy',
      })))
    }
    load()
  }, [])

  // Combined list for filters
  const combined = [...services, ...legacy]

  async function reactivate(item) {
    if (!window.confirm(`Reactivate "${item.client_name} — ${item.brand_name}"?`)) return
    setReactivating(item.id)
    if (item._source === 'services') {
      await supabase.from('services').update({ status: 'Running' }).eq('id', item.id)
      setServices(prev => prev.filter(s => s.id !== item.id))
    } else {
      // Legacy: insert back into services if client/brand exists
      const { data: client } = await supabase.from('clients').select('id').eq('name', item.client_name).maybeSingle()
      const { data: brand } = await supabase.from('brands').select('id').eq('name', item.project_name).maybeSingle()
      if (client && brand) {
        await supabase.from('services').insert([{
          client_id: client.id,
          brand_id: brand.id,
          service_type: item.category || 'Marketing',
          status: 'Running',
          value: Number(item.value) || 0,
          project_start: item.project_start || null,
          remarks: 'Reactivated on ' + new Date().toISOString().slice(0, 10),
        }]).select()
      }
      await supabase.from('paused_closed').delete().eq('id', item.id)
      setLegacy(prev => prev.filter(p => p.id !== item.id))
    }
    setReactivating(null)
  }

  async function remove(item) {
    if (!window.confirm('Delete this record permanently?')) return
    if (item._source === 'services') {
      await supabase.from('services').delete().eq('id', item.id)
      setServices(prev => prev.filter(s => s.id !== item.id))
    } else {
      await supabase.from('paused_closed').delete().eq('id', item.id)
      setLegacy(prev => prev.filter(p => p.id !== item.id))
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Archive — Paused / Stopped"
        subtitle={`${combined.length} archived projects`}
      />
      <div style={{ padding: 28, flex: 1 }}>
        <TableFilters filters={FILTERS} data={combined} onFiltered={handleFiltered} />
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Client</th>
                <th>Brand / Project</th>
                <th>Service type</th>
                <th>Value</th>
                <th>Status</th>
                <th>Paused / stopped on</th>
                <th>Remarks</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item._source + '-' + item.id}>
                  <td style={{ fontWeight: 500 }}>{item.client_name}</td>
                  <td>{item.brand_name}</td>
                  <td><span className={typeBadge(item.service_type)}>{item.service_type}</span></td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(item.value || 0)}</td>
                  <td><span className={statusBadge(item.status)}>{item.status}</span></td>
                  <td style={{ color: 'var(--text2)' }}>
                    {item.date_paused || item.created_at?.slice(0, 10) || '—'}
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: 12, maxWidth: 200 }}>
                    {item.remarks || item.reason || '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn"
                        style={{ padding: '4px 8px', fontSize: 11, color: 'var(--success)', borderColor: 'var(--success)' }}
                        onClick={() => reactivate(item)}
                        disabled={reactivating === item.id}
                      >
                        {reactivating === item.id ? '…' : 'Reactivate'}
                      </button>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(item)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No paused or stopped projects</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
